/**
 * Pointer-Events-based aim controller.
 *
 * Spec requirements covered here:
 *   3. Cross-platform input — uses pointerdown / pointermove / pointerup
 *      (NOT mouse events), and `setPointerCapture` so the drag continues even
 *      when the finger leaves the canvas. The canvas CSS sets touch-action: none
 *      (see index.css) so iOS/Android don't hijack the gesture for scroll/zoom.
 *
 * Drag interpretation:
 *   - The user touches the canvas anywhere → that's the kick anchor.
 *   - They drag away from the anchor → vector encodes power and angle.
 *   - Lateral motion of the *finger itself during the drag* (third axis)
 *     encodes curl: dragging straight produces zero curl, swinging the
 *     finger right while dragging up produces in-swinging curl, etc.
 *
 * Geometry, in canvas CSS pixels:
 *   - dx (positive right) → angle      (-1 …+1 over MAX_AIM_PX)
 *   - -dy (positive up)   → power      ( 0 …+1 over MAX_POWER_PX)
 *   - curlAccumulator     → curl       (-1 …+1 over MAX_CURL_PX)
 */

import type { AimInput } from "./types";

const MAX_AIM_PX = 120;
const MAX_POWER_PX = 220;
const MAX_CURL_PX = 80;

export interface AimController {
  dispose: () => void;
}

interface DragState {
  pointerId: number;
  anchorX: number;
  anchorY: number;
  lastX: number;
  curl: number;
}

export interface AimEvents {
  onAimPreview?: (input: AimInput) => void;
  onAimCommit: (input: AimInput) => void;
  onCancel?: () => void;
}

function clamp01(n: number): number { return Math.max(-1, Math.min(1, n)); }
function clamp1(n: number): number { return Math.max(0, Math.min(1, n)); }

function compute(drag: DragState, x: number, y: number): AimInput {
  const dx = x - drag.anchorX;
  const dy = y - drag.anchorY;
  return {
    angle: clamp01(dx / MAX_AIM_PX),
    power: clamp1(-dy / MAX_POWER_PX),
    curl: clamp01(drag.curl / MAX_CURL_PX),
  };
}

/**
 * Attach the aim controller to a canvas. Returns a dispose handle.
 * Aim is reported continuously while dragging (preview) and once on release
 * (commit). Releasing without any movement, or with zero power, calls onCancel.
 */
export function attachAim(canvas: HTMLCanvasElement, events: AimEvents): AimController {
  let drag: DragState | null = null;

  const localXY = (e: PointerEvent): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: PointerEvent): void => {
    if (drag) return;
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    const { x, y } = localXY(e);
    drag = { pointerId: e.pointerId, anchorX: x, anchorY: y, lastX: x, curl: 0 };
  };

  const onMove = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    e.preventDefault();
    const { x, y } = localXY(e);
    // Curl accumulates lateral motion regardless of overall drag direction —
    // this gives the "whip the finger sideways while dragging up" feel.
    drag.curl += x - drag.lastX;
    drag.lastX = x;
    if (events.onAimPreview) events.onAimPreview(compute(drag, x, y));
  };

  const onUp = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    e.preventDefault();
    const { x, y } = localXY(e);
    const input = compute(drag, x, y);
    canvas.releasePointerCapture(e.pointerId);
    drag = null;
    if (input.power < 0.05) {
      events.onCancel?.();
      return;
    }
    events.onAimCommit(input);
  };

  const onCancel = (e: PointerEvent): void => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    drag = null;
    events.onCancel?.();
  };

  canvas.addEventListener("pointerdown", onDown);
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerup", onUp);
  canvas.addEventListener("pointercancel", onCancel);

  return {
    dispose: (): void => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onCancel);
    },
  };
}
